import Icon from "@mdi/react";
import { useState } from "react";
import { mdiDrag, mdiPen, mdiPlus } from "@mdi/js";
import { Link, useTitle } from "easy-page-router/react";

import { useHeaders, useSSRHook } from "../../ssrh/client";
import { useUser } from "../User";
import { Header, Loading, Remove, Separator } from "../components";
import { api } from "../services/api";

import { PageFolder, PageInfo, PageMeta } from "../../types";
import Modal from "react-responsive-modal";
import { DraggableContextType } from "react-tiny-dnd/lib/types";
import { Draggable, moveItems, useDraggable, useDraggableContext } from "react-tiny-dnd";
import LastProjects from "../components/LastProjects";
import Footer from "../components/Footer";

const emptyPageMeta: PageMeta = {
    _id: "",
    type: "page",
    order: 0,
    pageId: "",
    title: "",
    url: "",
};

const emptyPageFolder: PageFolder = {
    _id: "",
    type: "folder",
    name: "",
    order: 0,
};

export default function AboutUs() {
    const user = useUser();

    const [pages, error, isLoading, reload] = useSSRHook<PageInfo[]>("/api/pages");

    const [newPage, setNewPage] = useState<null | PageInfo>(null);
    const [changeOrder, setChangeOrder] = useState(false);
    const [message, setMessage] = useState("");

    const title = "O nás | TSO Krkonoše";
    useTitle(title);
    useHeaders({
        title,
        description: "O nás - technický svazek obcí Krkonoše",
        image: window.location.origin + "/logo.png",
        canonical: window.location.origin + window.location.pathname,
    });

    async function handleAddPageInfo() {
        if (newPage) {
            if (newPage.type === "folder") {
                if (newPage._id === "") {
                    const [, error] = await api.addPageFolder(newPage);

                    if (error) {
                        setMessage("Přidat složku stránek selhalo");
                        return;
                    }
                } else {
                    const [, error] = await api.updatePageInfo(newPage);

                    if (error) {
                        setMessage("Upravit složku stránek selhalo");
                        return;
                    }
                }

                reload();
                setNewPage(null);
                setMessage("");
            }

            if (newPage.type === "page") {
                const [, error] = await api.addPage({
                    ...newPage,
                    content: "",
                    files: [],
                });

                if (error) {
                    setMessage("Přidat stránku selhalo");
                    return;
                }

                reload();
                setNewPage(null);
                setMessage("");
            }
        }
    }

    async function handleRemove(pageFolderId: string) {
        const [, error] = await api.removePageFolder({ pageFolderId });

        if (error) {
            setMessage("Smazání stránky selhalo");
            return;
        }

        reload();
        setNewPage(null);
        setMessage("");
    }

    // drag list
    async function onDrop(dragIndex: number, overIndex: number) {
        if (!pages) return;

        const nextPages = moveItems(pages, dragIndex, overIndex)
            .map((page, order) => ({ ...page, order }));

        for (const page of nextPages) {
            await api.updatePageInfo(page);
        }
        setMessage("Pořadí stránek / záložek upraveno");
        reload();
    };

    const context = useDraggableContext({ onDrop });

    let withoutFolder = true;

    return (
        <>
            <main>
                <Header background="numbers" title="O nás">
                    <Link to="/o-nas">O nás</Link>
                </Header>
                <div className="content">
                    <section>
                        <div className="page">
                            <h1>O nás</h1>
                            <Separator />

                            {user.isAdmin && message && <p className="alert">{message}</p>}

                            {user.isAdmin && (
                                <div className="admin">
                                    <button onClick={() => setNewPage(emptyPageMeta)}>
                                        <Icon path={mdiPlus} size={1} /> Přidat stránku
                                    </button>
                                    <button onClick={() => setNewPage(emptyPageFolder)}>
                                        <Icon path={mdiPlus} size={1} /> Přidat složku stránek
                                    </button>
                                    <button onClick={() => {
                                        setChangeOrder(!changeOrder);
                                        setMessage("");
                                    }}>
                                        <Icon path={mdiPlus} size={1} />
                                        {changeOrder ? "Uložit změny" : "Udělat změny"}
                                    </button>

                                    <Modal
                                        open={newPage !== null}
                                        onClose={() => setNewPage(null)}
                                        center
                                        styles={{ modal: { width: "500px" } }}
                                    >
                                        {message && <p className="alert">{message}</p>}

                                        {newPage?.type === "folder" && <>
                                            <h2>Přidat složku stránek</h2>
                                            <input
                                                type="text"
                                                placeholder="Název"
                                                value={newPage.name}
                                                onChange={(e) => setNewPage({ ...newPage, name: e.target.value })}
                                            />
                                            <button onClick={handleAddPageInfo}>Přidat</button>
                                        </>}
                                        {newPage?.type === "page" && <>
                                            <h2>Přidat stránku</h2>
                                            <input
                                                type="text"
                                                placeholder="Nádpis stránnky"
                                                value={newPage.title}
                                                onChange={(e) => setNewPage({ ...newPage, title: e.target.value })}
                                            />
                                            <button onClick={handleAddPageInfo}>Přidat</button>
                                        </>}
                                    </Modal>
                                </div>
                            )}

                            {isLoading && <Loading />}
                            {error && (
                                <p className="alert">
                                    Obsah se bohužel nepodařilo načíst 🙁
                                </p>
                            )}
                            {pages && (
                                <div className="list slim no-select">
                                    {!changeOrder && pages?.map((page) => {
                                        if (page.type === "folder") withoutFolder = false;

                                        return <div
                                            key={page._id}
                                            className={`item ${page.type} ${withoutFolder ? "without-folder" : ""}`}
                                        >
                                            {page.type === "folder" && <div className="folder">
                                                <h2>{page.name}</h2>
                                            </div>}
                                            {page.type === "page" && <Link to={`/o-nas/${page.url}`}>
                                                {page.title}
                                            </Link>}
                                        </div>
                                    })}

                                    {changeOrder && pages?.map((page, i) => <DraggableItem
                                        key={page._id}
                                        context={context}
                                        page={page}
                                        index={i}
                                        className={`item ${page.type}`}
                                        onChange={() => page.type === "folder" && setNewPage(page)}
                                        onRemove={() => page.type === "folder" && handleRemove(page._id)}
                                    />)}
                                </div>
                            )}
                        </div>
                    </section>
                </div>
                <LastProjects />
            </main>
            <Footer />
        </>
    );
}

function DraggableItem(
    { context, index, className, page, onChange, onRemove }: {
        context: DraggableContextType;
        index: number;
        page: PageInfo;
        className: string;
        onChange: () => void;
        onRemove: () => void;
    },
) {
    const { listeners, isDragging } = useDraggable(context, index);

    return (
        <Draggable
            context={context}
            index={index}
            dividerClassName="dnd-divider"
        >
            <div className={className} style={{ opacity: isDragging ? 0.4 : 1 }}>
                <div className="dnd-button" {...listeners}>
                    <Icon path={mdiDrag} size={2} />
                </div>
                {page.type === "page" && page.title}
                {page.type === "folder" && <>
                    <div className="folder"><h2>{page.name}</h2></div>
                    <div className="grow" />
                    <button className="icon" onClick={onChange}>
                        <Icon path={mdiPen} size={1} />
                    </button>
                    <Remove onRemove={onRemove} />
                </>}
            </div>
        </Draggable>
    );
}
